/* eslint-disable no-inline-comments */
/* eslint-disable line-comment-position */
/* eslint-disable max-len */
import { HorizontalAlignment } from '../../shared.enums';
import { Island } from '../../islands/island.class';
import { Panel } from '../panel.class';

/**
 * Horizontal layout budget, in inches.
 *
 * These are LAYOUT minimums — the air a panel must reserve for a placement to
 * be geometrically possible. They are deliberately NOT the rules-engine
 * clearances in `rules-engine/validations/spacing-limits/` (4.5" between
 * appliances, 5" from an edge), which are product requirements validated after
 * the fact and reported to the customer as notifications. A design can satisfy
 * this budget and still raise those warnings; that is the intended split.
 */
export const PANEL_SPACING = {
	/** Air between two adjacent appliance groups. */
	betweenGroups: 1,
	/** Total allowance for BOTH panel ends combined (not per end). */
	edges: 2
} as const;

/**
 * Width consumed at each end of a panel by an island connected in front of it.
 *
 * When islands connect at an angle the neighbour's DEPTH eats into this panel's
 * usable run, so the panel's own `size.width` overstates the space available to
 * appliances. `distributeApplianceGroupsHorizontally` has always accounted for
 * this when positioning groups — it clamps to `±width/2` adjusted by these
 * overlaps — which meant the layout and the fit check disagreed about how wide
 * the panel was. On an L-shaped kitchen whose 93" bar island meets a 28"-deep
 * grill island at zero offset, the usable run is 65", and the fit check was
 * budgeting the full 93".
 *
 * `offset` slides the neighbour along the connection: once it exceeds the
 * neighbour's depth, nothing overlaps any more.
 */
export const getPanelOverlapCfn = (panel: Panel): { left: number; right: number; total: number } => {
	const island = panel.refMap.island as Island | null;
	if (!island) return { left: 0, right: 0, total: 0 };

	const overlapFor = (align: HorizontalAlignment): number => {
		const connection = island.connections.children.find((c) => c.sourcePanelId === panel.id && c.horizontalAlign === align);
		if (!connection) return 0;
		const depth = connection.targetIsland?.islandSize?.depth ?? 0;
		const offset = Math.abs(connection.offset ?? 0);
		return offset < depth ? depth - offset : 0;
	};

	const left = overlapFor(HorizontalAlignment.left);
	const right = overlapFor(HorizontalAlignment.right);

	return { left, right, total: left + right };
};

/** Panel width actually available to appliances, i.e. minus connected-island overlap. */
export const getUsablePanelWidthCfn = (panel: Panel): number => Math.max(0, panel.size.width - getPanelOverlapCfn(panel).total);

/**
 * Width already taken by this panel's appliance groups.
 *
 * `maxOutsideWidth` (the widest OUTSIDE dimension in the group) is used rather
 * than the cutout width, so a group is charged the space its trim actually
 * occupies. Vent and passthrough groups are included: they are physically on
 * the panel even though `distributeApplianceGroupsHorizontally` won't reposition
 * them.
 */
export const getOccupiedWidthCfn = (panel: Panel): number => panel.panelItemGroups.children.reduce((sum, g) => sum + g.maxOutsideWidth, 0);

/**
 * Widest new appliance group this panel can still take.
 *
 * Inverts the placement budget so callers never have to restate it:
 *
 *   required = occupied + W + edges + betweenGroups × gaps      (gaps = existing groups)
 *   fits when required <= usable
 *
 * An empty panel has no gaps, so it only pays the edge allowance.
 */
export const getAvailableWidthCfn = (panel: Panel): number => {
	const gaps = panel.panelItemGroups.children.length;
	const budget = getUsablePanelWidthCfn(panel) -
		getOccupiedWidthCfn(panel) -
		PANEL_SPACING.edges -
		(PANEL_SPACING.betweenGroups * gaps);

	return Math.max(0, budget);
};

/**
 * Narrowest this panel could be and still hold what is already on it —
 * the floor for a resize.
 */
export const getMinimumPanelWidthCfn = (panel: Panel): number =>
	getRequiredPanelWidthCfn(panel.panelItemGroups.children.map((g) => g.maxOutsideWidth), getPanelOverlapCfn(panel).total);

/**
 * The same floor for a HYPOTHETICAL set of columns — what a panel would need
 * if these widths were on it. `getMinimumPanelWidthCfn` is this applied to the
 * live panel; the drag preview applies it to "the target panel plus the column
 * being dragged over" before anything moves.
 */
export const getRequiredPanelWidthCfn = (groupWidths: Array<number>, overlapTotal: number): number => {
	const groups = groupWidths.length;
	const gaps = groups > 1 ? groups - 1 : 0;

	return groupWidths.reduce((sum, w) => sum + w, 0) +
		PANEL_SPACING.edges +
		(PANEL_SPACING.betweenGroups * gaps) +
		overlapTotal;
};
